import { Dispatch } from '@reduxjs/toolkit';
import { EventTypes } from '../constants/event';
import {
  InfoTransmissionMessage,
  messagingActions,
} from '../reducers/messagingSlice';
import { ParsedMenuItem, ParsedModifierGroup } from '../types/menu';
import { getOptionModifierGroups } from './lazyOptionBuild';

export const getModifierAndModGroupFromHypotheses = (
  topLevelMenuItem: ParsedMenuItem | undefined,
  modId: string | undefined,
  modGroupId: string
) => {
  if (!topLevelMenuItem || !modId || !topLevelMenuItem.modifierGroups) {
    return { modGroup: undefined, modifier: undefined };
  }

  // HITL-1748: below the top level this is an option whose groups may not be
  // built yet.
  const modGroup = Object.values(
    getOptionModifierGroups(topLevelMenuItem)
  ).find((modGroup) => modGroup.id === modGroupId);
  const modifier = modGroup
    ? (Object.values(modGroup.menuItems).find(
        (item) => item.id === modId
      ) as ParsedMenuItem)
    : undefined;

  return { modGroup, modifier };
};

/** One of this item's own modifier groups, by id. */
export const findModGroup = (
  menuItem: ParsedMenuItem | undefined,
  modGroupId: string
): ParsedModifierGroup | undefined =>
  menuItem?.modifierGroups
    ? Object.values(getOptionModifierGroups(menuItem)).find(
        (modGroup) => modGroup.id === modGroupId
      )
    : undefined;

/**
 * HITL-1758 — the group a hypothesis modifier actually belongs to.
 *
 * `handleDuplicateModifierGroupIds` suffixes a single-choice group that the AI
 * names more than once: four `sugar` rows under `hot_coffee_sweetener` arrive
 * as `hot_coffee_sweetener_1..4`. On a dozen those are real slot groups. On a
 * coffee they are not, and looking them up dropped every one of the four. So a
 * suffixed id that names no group falls back to its bare group when that one
 * exists; anything else is returned untouched.
 */
export const resolveHypothesisModGroupId = (
  menuItem: ParsedMenuItem | undefined,
  modGroupId: string
): string => {
  if (findModGroup(menuItem, modGroupId)) return modGroupId;
  const match = /^(.+)_\d+$/.exec(modGroupId);
  return match && findModGroup(menuItem, match[1]) ? match[1] : modGroupId;
};

export const getModifierAndModGroupFromHypothesesByName = (
  topLevelMenuItem: ParsedMenuItem | undefined,
  modifierName: string | undefined,
  modGroupId: string
) => {
  if (!topLevelMenuItem || !modifierName || !topLevelMenuItem.modifierGroups) {
    return { modGroup: undefined, modifier: undefined };
  }

  // HITL-1748: below the top level this is an option whose groups may not be
  // built yet.
  const modGroup = Object.values(
    getOptionModifierGroups(topLevelMenuItem)
  ).find((modGroup) => modGroup.id === modGroupId);
  const modifier = modGroup
    ? (Object.values(modGroup.menuItems).find(
        (item) => item.name === modifierName
      ) as ParsedMenuItem)
    : undefined;

  return { modGroup, modifier };
};

export const sendAIInfo = (metadata: any, dispatch: Dispatch) => {
  const payload: Partial<InfoTransmissionMessage> = {
    data: { metadata, type: EventTypes.hypothesis, message: '' },
  };
  dispatch(messagingActions.sendInfo(payload as InfoTransmissionMessage));
};
